import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import Field from '../../components/Field';
import Icon from '../../components/Icon';
import { ErrorBanner, InfoBanner } from '../../components/Feedback';
import useForm from '../../hooks/useForm';
import * as v from '../../validation/rules';
import { PASSWORD_CODES } from '../../validation/serverCodes';
import { api } from '../../api/client';

/** Landing page for the emailed reset link: /reset-password?token=… */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const form = useForm({
    initialValues: { newPassword: '', confirmPassword: '' },
    validators: { newPassword: v.password, confirmPassword: v.required('Confirmation') },
    crossField: (values) =>
      values.confirmPassword && values.newPassword !== values.confirmPassword
        ? { confirmPassword: 'Passwords do not match' }
        : {},
  });

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await api.resetPassword({ resetToken: token, newPassword: values.newPassword });
      setDone(true);
    } catch (err) {
      if (!form.applyServerErrors(err, PASSWORD_CODES)) setError(err);
    }
  });

  if (!token) {
    return (
      <AuthLayout>
        <h2>Reset link incomplete</h2>
        <p className="sub">This link is missing its token. Request a new one from the sign-in screen.</p>
        <Link to="/login" className="btn btn-primary">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form {...form.formProps} onSubmit={submit}>
        <h2>Choose a new password</h2>
        <p className="sub">Resetting signs you out of every device.</p>
        <ErrorBanner error={error} onDismiss={() => setError(null)} />
        {done ? (
          <>
            <InfoBanner>Password updated.</InfoBanner>
            <Link to="/login" className="btn btn-primary btn-block">
              Sign in
              <Icon name="arrow" size={15} />
            </Link>
          </>
        ) : (
          <div className="stack">
            <Field label="New password" id="newPassword" required hint={v.PASSWORD_HINT}
                   error={form.errorFor('newPassword')}>
              <input className="input" type="password" autoComplete="new-password" maxLength={100}
                     {...form.field('newPassword')} />
            </Field>
            <Field label="Confirm new password" id="confirmPassword" required
                   error={form.errorFor('confirmPassword')}>
              <input className="input" type="password" autoComplete="new-password" maxLength={100}
                     {...form.field('confirmPassword')} />
            </Field>
            <button type="submit" className="btn btn-primary btn-block" disabled={form.submitting}>
              {form.submitting ? 'Saving…' : 'Set password'}
              <Icon name="check" size={15} />
            </button>
          </div>
        )}
      </form>
    </AuthLayout>
  );
}
